import type {
    ComposableOptionsBase,
    ReturnEligibility,
    ReturnRequest,
    CreateReturnRequest
} from '~/types';
import type {
    PaginatedPayload,
    CustomPagination
} from 'x1-base/types';
import {
    useCustomFetch
} from '~/composables';
import { useApiUtils } from 'x1-base/composables';
import type { NitroFetchOptions } from 'nitropack';
import type { NuxtError } from '#app';

export interface UseReturnsOptions extends ComposableOptionsBase {}

export function useReturns(options: Partial<UseReturnsOptions> = {}) {
    const apiBasePath = `/v1/returns`;
    const useApiUtilsObj = useApiUtils();
    const $_fetch = useCustomFetch();

    async function getEligibility(orderUid: string): Promise<ReturnEligibility | Error> {
        const url = `${apiBasePath}/eligibility/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {
                order: orderUid
            }
        };

        try {
            return await $_fetch<ReturnEligibility>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function createReturn(returnObj: CreateReturnRequest): Promise<ReturnRequest | NuxtError> {
        const url = `${apiBasePath}/`;
        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body: returnObj
        };

        try {
            return await $_fetch<ReturnRequest>(url, config);
        } catch (err) {
            return useApiUtilsObj.getNuxtErrorObj(err);
        }
    }

    async function getReturn(rmaUid: string): Promise<ReturnRequest | Error> {
        const url = `${apiBasePath}/${rmaUid}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET'
        };

        try {
            return await $_fetch<ReturnRequest>(url, config);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function getOrderReturns(orderUid: string): Promise<PaginatedPayload<ReturnRequest> | Error> {
        const url = `${apiBasePath}/`;
        const config: NitroFetchOptions<string> = {
            method: 'GET',
            query: {
                order: orderUid
            }
        };

        try {
            const response = await $_fetch<CustomPagination<ReturnRequest>>(url, config);
            return useApiUtilsObj.customToPaginatedPayload(response);
        } catch (err) {
            return useApiUtilsObj.getErrorObj(err);
        }
    }

    async function cancelReturn(rmaUid: string): Promise<ReturnRequest | NuxtError> {
        const url = `${apiBasePath}/${rmaUid}/cancel/`;
        const config: NitroFetchOptions<string> = {
            method: 'PATCH'
        };

        try {
            return await $_fetch<ReturnRequest>(url, config);
        } catch (err) {
            return useApiUtilsObj.getNuxtErrorObj(err);
        }
    }

    return {
        getEligibility,
        createReturn,
        getReturn,
        getOrderReturns,
        cancelReturn
    };
}
